/**
 * The router: sign-in and other public pages, then the signed-in session, then the shell.
 * Built from the tables in routes.ts; layouts are injectable so a test can resolve every
 * address without signing in.
 */
import type { ComponentType } from 'react';
import type { RouteObject } from 'react-router-dom';
import { PUBLIC_FRAME_ROUTES, SESSION_FRAME_ROUTES, SHELL_ROUTES, SIGN_IN_PATHS, type AppRoute } from './routes';
import { ComingSoonPage, FrameComingSoonPage, NotFoundPage, SignInPage, TenantLinkPage } from './pages/simple-pages';
import { AppShellLayout, NOT_FOUND_ROUTE } from './shell/AppShellLayout';
import { SessionLayout } from './session/SessionLayout';

export interface RouteLayouts {
  readonly Session: ComponentType;
  readonly Shell: ComponentType;
}

/** What `useMatches()` finds on a shell route. */
export interface RouteHandle {
  readonly route: AppRoute;
}

export function buildRoutes(layouts: RouteLayouts = { Session: SessionLayout, Shell: AppShellLayout }): RouteObject[] {
  const { Session, Shell } = layouts;
  return [
    ...SIGN_IN_PATHS.map((path): RouteObject => ({ path, element: <SignInPage /> })),
    { path: '/t/:workspaceId', element: <TenantLinkPage /> },
    ...PUBLIC_FRAME_ROUTES.map((r): RouteObject => ({ path: r.path, element: <FrameComingSoonPage route={r} />, handle: { route: r } satisfies RouteHandle })),
    {
      element: <Session />,
      children: [
        ...SESSION_FRAME_ROUTES.map((r): RouteObject => ({ path: r.path, element: <FrameComingSoonPage route={r} />, handle: { route: r } satisfies RouteHandle })),
        {
          element: <Shell />,
          children: [
            ...SHELL_ROUTES.map((r): RouteObject => ({ path: r.path, element: <ComingSoonPage route={r} />, handle: { route: r } satisfies RouteHandle })),
            { path: '*', element: <NotFoundPage />, handle: { route: NOT_FOUND_ROUTE } satisfies RouteHandle },
          ],
        },
      ],
    },
  ];
}
